import React, { useEffect, useState } from 'react'

import { Box, Stack, Typography } from '@mui/material'

import { font, space } from 'settings/tokens'
import { IconBadge } from './IconBadge'
import { PendingRing } from './PendingRing'

/**
 * Diameter of the badge. Matches `IconBadge`'s own default; kept as a local
 * constant only because `PendingRing` needs the number to size itself.
 */
const CIRCLE = 88
/** Check glyph size inside it. */
const CHECK = 40
/** How long the spinner shows before the check draws. */
const PENDING_MS = 500
/** Check draw duration. */
const DRAW_MS = 420

interface SuccessStateProps {
  title: string
  description?: React.ReactNode
  /**
   * Hold the spinner. Pass `true` while the request is genuinely in flight and
   * flip it to `false` on success. Omit it and the badge shows a brief spinner
   * on mount before the check draws — for callers that only mount this AFTER
   * their mutation resolves, which is every current one.
   */
  pending?: boolean
}

/**
 * The confirmation badge + copy every "that worked" screen uses.
 *
 * Extracted for VERA-315 (§36) because two of them had already drifted, and the
 * differences were not subtle: the Report-missing success drew its check as the
 * TEXT CHARACTER "✓" at 40px bold inside an 80px circle under a 20px/600 title,
 * while the gift/scan success drew an SVG check in an 88px circle under a
 * 16px/500 title. Same moment in two flows, two badges and two type scales.
 *
 * ## The spinner, then the draw
 *
 * A success screen that arrives fully formed gives the eye nothing to follow and
 * reads as a jump cut. A beat of spinner followed by the check drawing itself
 * covers the transition and makes the confirmation feel earned. It is also
 * honest when `pending` is driven by a real request.
 *
 * ## Why the resting state is the FINISHED check
 *
 * The path rests at `strokeDashoffset: 0` — drawn — and the keyframes animate
 * FROM the undrawn state. This is the rule `utils/pageTransition` already
 * establishes for this app: an environment that does not run the animation must
 * degrade to "it is simply there", never to an invisible element. (The dev
 * preview pane is exactly that environment — it reports `visibilityState:
 * hidden` and runs no animation at all.) Written the other way round, a
 * skipped animation would leave a permanently blank badge.
 */
export const SuccessState = ({ title, description, pending }: SuccessStateProps) => {
  const [selfPending, setSelfPending] = useState(pending ?? true)

  useEffect(() => {
    if (pending !== undefined) {
      setSelfPending(pending)
      return
    }
    const t = window.setTimeout(() => setSelfPending(false), PENDING_MS)
    return () => window.clearTimeout(t)
  }, [pending])

  /*
   * ⚠️ The timer starts on MOUNT, so this must not be mounted before it is seen.
   *
   * Bottom sheets in this app stay mounted while closed (`DrawerPopup` renders
   * its child regardless of `open`), so a success badge left inside a closed
   * sheet mounts at PAGE LOAD — its spinner elapses minutes before anyone opens
   * the sheet, and the user is shown a static check. Measured exactly that on
   * the gift/scan drawer: a drawn check 150ms after the flow started, and no
   * spinner at any point.
   *
   * Callers therefore gate this on the sheet being open (see
   * `genericSuccessDrawer`), or mount it only on the success branch (see
   * `ReportMissingDrawer`, where `isSubmitted` swaps the whole child).
   */

  return (
    <Stack spacing={3} sx={{ alignItems: 'center', textAlign: 'center' }}>
      {/*
        The disc, its grey, its faint edge and its brand-toned glyph colour now
        all live in `IconBadge` (§36) — this file was one of five hand-rolls of
        the same badge, two of which had drifted to a red-tinted 56px variant
        with a hardcoded hex. The reasoning that used to sit on the `border`
        line moved into that component with it. The check is passed as a CHILD
        rather than as an `icon` because it is not a lucide glyph: it draws
        itself, and it alternates with the pending ring.
      */}
      <IconBadge size={CIRCLE}>
        {selfPending ? (
          /*
            VERA-304: the ring that used to be written inline here is now
            `PendingRing`, shared with `ScanTag`'s scan chip (§36). Every reason it
            is sized to the badge's OUTER diameter rather than something smaller —
            and positioned absolutely rather than flowed — moved into that file
            with it. This comment's predecessor already said the two were "the same
            badge in two flows and must not drift again"; they now cannot.
          */
          <PendingRing size={CIRCLE} />
        ) : (
          <Box
            component='svg'
            width={CHECK}
            height={CHECK}
            viewBox='0 0 24 24'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
            sx={{
              '@keyframes veraCheckDraw': {
                from: { strokeDashoffset: 32 },
                to: { strokeDashoffset: 0 },
              },
              '& path': {
                strokeDasharray: 32,
                // Rests DRAWN — see the note above.
                strokeDashoffset: 0,
                animation: `veraCheckDraw ${DRAW_MS}ms cubic-bezier(0.65, 0, 0.35, 1) both`,
              },
            }}
          >
            <path
              d='M20 6L9 17L4 12'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </Box>
        )}
      </IconBadge>

      <Stack spacing={1} alignItems='center'>
        <Typography sx={{ fontSize: font.body, fontWeight: 500, color: 'text.primary' }}>
          {title}
        </Typography>
        {description && (
          <Typography
            sx={{
              fontSize: font.label,
              color: 'text.secondary',
              lineHeight: 1.5,
              maxWidth: 320,
              px: `${space.sm}px`,
            }}
          >
            {description}
          </Typography>
        )}
      </Stack>
    </Stack>
  )
}

export default SuccessState
